
import { Component, Prop, Mixins, Provide } from "vue-property-decorator";
import { CreateElement } from "vue";
import { mapState } from "vuex";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { ActionTypes } from "@/data/enums";

// Types
import { IProvider } from "@/models/providers";
import {
  IBillableEntitiesProvider,
  IBillableEntitiesProviderMethods
} from "@/models/providers/billableEntities";
import { IState } from "@/store";
import { IClient } from "@/models/clients";
import { ICompany } from "@/models/companies";
import { IAddress } from "@/models/addresses";

@Component({
  computed: {
    ...mapState({
      addressesScope(state: IState, getters) {
        return getters[`data/${DataModules.CLIENTS_ADDRESSES}/clientScope`](
          this.clientId
        );
      },
      addresses(state: IState, getters) {
        return this.showAddresses
          ? _.orderBy(
              getters[`data/listArray`]({
                storeModule: DataModules.CLIENTS_ADDRESSES,
                scope: this.addressesScope
              }),
              ["created_at", "asc"]
            )
          : [];
      },
      companiesScope(state: IState, getters) {
        return getters[`data/${DataModules.CLIENTS_COMPANIES}/clientScope`](
          this.clientId
        );
      },
      companies(state: IState, getters) {
        return this.showCompanies
          ? _.orderBy(
              getters[`data/listArray`]({
                storeModule: DataModules.CLIENTS_COMPANIES,
                scope: this.companiesScope
              }),
              ["created_at", "asc"]
            )
          : [];
      }
    })
  }
})
export default class BillableEntitiesProvider extends Mixins(RequestPromises)
  implements IProvider {
  // Map State

  public addresses!: IAddress[];
  public addressesScope!: string;
  public companies!: ICompany[];
  public companiesScope!: string;

  // Props

  @Prop({ required: true })
  public readonly clientId!: IClient["id"];

  @Prop({ default: false })
  public handleGuarding!: boolean;

  @Prop({ default: false })
  public handleLoading!: boolean;

  @Prop({ default: false })
  public handleRequestError!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public showAddresses!: boolean;

  @Prop({ default: true })
  public showCompanies!: boolean;

  // Data

  public isLoading = true;
  public isReloading = false;
  public hasRequestError = false;
  public isRefreshingAddresses = false;
  public isRefreshingCompanies = false;

  // Life-cycle methods

  public async created() {
    if (!this.contentIsAvailable) return;
    await this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.CLIENTS_COMPANIES,
      scope: this.companiesScope,
      vm: this
    });

    this.$store.dispatch("data/binList", {
      storeModule: DataModules.CLIENTS_ADDRESSES,
      scope: this.addressesScope,
      vm: this
    });
  }

  // Methods

  public async loadData() {
    try {
      await Promise.all([
        this.showAddresses
          ? this.getClientAddressesData(this.isReloading)
          : () => Promise.resolve(),
        this.showCompanies
          ? this.getClientCompaniesData(this.isReloading)
          : () => Promise.resolve(),
        this.$store.dispatch("constants/getCountries")
      ]);
      this.hasRequestError = false;
    } catch (error) {
      this.hasRequestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData();
  }

  public getClientAddressesData(ignoreStored) {
    return this.$store.dispatch(`data/${DataModules.CLIENTS_ADDRESSES}/list`, {
      id: this.clientId,
      scope: this.addressesScope,
      ignoreStored,
      params: {
        with: "country"
      },
      vm: this
    });
  }

  public getClientCompaniesData(ignoreStored = false) {
    return this.$store.dispatch(`data/${DataModules.CLIENTS_COMPANIES}/list`, {
      id: this.clientId,
      scope: this.companiesScope,
      ignoreStored,
      params: {
        with: "address.country"
      },
      vm: this
    });
  }

  public manageClientAddress({
    address = {} as IAddress,
    searchedValue = "",
    config = {
      events: {
        success: this.refreshAddresses
      }
    }
  }: {
    address?: IAddress;
    searchedValue?: string;
    config: any;
  }) {
    if (_.isEmpty(address) ? !this.canCreateAddress : !this.canUpdateAddress)
      return;

    return this.$store.dispatch(
      `data/${DataModules.CLIENTS_ADDRESSES}/openAddressModal`,
      {
        props: {
          clientId: this.clientId,
          ...(_.isEmpty(address)
            ? {}
            : {
                actionType: address.id
                  ? ActionTypes.UPDATE
                  : ActionTypes.CREATE,
                addressId: address.id,
                data: this.$_.pick(address, [
                  "name",
                  "address_1",
                  "address_2",
                  "country_id",
                  "region_id",
                  "city",
                  "postcode",
                  "type"
                ])
              }),
          ...(searchedValue ? { searchedValue } : {})
        },
        width: 640,
        ...config
      }
    );
  }

  public deleteClientAddress(address: IAddress) {
    if (!this.canDeleteAddress) return;

    return this.$store.dispatch(
      `data/${DataModules.CLIENTS_ADDRESSES}/confirmDelete`,
      {
        address,
        onConfirm: () => {
          this.isReloading = true;
          this.loadData();
        }
      }
    );
  }

  public async setDefaultAddress(address: IAddress) {
    if (!this.canUpdateAddress) return;
    try {
      await this.$store.dispatch(
        `data/${DataModules.CLIENTS_ADDRESSES}/update`,
        {
          id: this.clientId,
          address_id: address.id,
          data: { default: true }
        }
      );
      await Promise.all([
        this.refreshAddresses(),
        this.$store.dispatch("toast/show", {
          message: this.$t("_sentence.default_address_updated")
        })
      ]);
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public async refreshAddresses() {
    this.isRefreshingAddresses = true;

    try {
      await this.getClientAddressesData(true);
      this.hasRequestError = false;
      this.$emit("dataRefreshed");
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isRefreshingAddresses = false;
    }
  }

  public manageClientCompany({
    company = {} as ICompany,
    config = {
      events: {
        success: this.refreshCompanies
      }
    }
  }: {
    company?: ICompany;
    config?: any;
  }) {
    if (_.isEmpty(company) ? !this.canCreateCompany : !this.canUpdateCompany)
      return;

    return this.$store.dispatch(
      `data/${DataModules.CLIENTS_COMPANIES}/openCompanyModal`,
      {
        props: {
          clientId: this.clientId,
          ...(_.isEmpty(company)
            ? {}
            : {
                actionType: company.id
                  ? ActionTypes.UPDATE
                  : ActionTypes.CREATE,
                companyId: company.id,
                data: this.$_.pick(company, [
                  "name",
                  "vat_number",
                  "reg_number",
                  "address_id",
                  "email_id",
                  "phone_id"
                ])
              })
        },
        width: 640,
        ...config
      }
    );
  }

  public async setDefaultCompany(company: ICompany) {
    if (!this.canUpdateCompany) return;
    try {
      await this.$store.dispatch(
        `data/${DataModules.CLIENTS_COMPANIES}/update`,
        {
          id: this.clientId,
          company_id: company.id,
          data: { default: true }
        }
      );
      await Promise.all([
        this.refreshCompanies(),
        this.$store.dispatch("toast/show", {
          message: this.$t("_sentence.default_company_updated")
        })
      ]);
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public async refreshCompanies() {
    this.isRefreshingCompanies = true;

    try {
      await this.getClientCompaniesData(true);
      this.hasRequestError = false;
      this.$emit("dataRefreshed");
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isRefreshingCompanies = false;
    }
  }

  public deleteClientCompany(company: IAddress | ICompany) {
    if (!this.canDeleteCompany) return;

    return this.$store.dispatch(
      `data/${DataModules.CLIENTS_COMPANIES}/confirmDelete`,
      {
        company,
        onConfirm: () => {
          this.isReloading = true;
          this.loadData();
        }
      }
    );
  }

  // Computed

  public get entities() {
    return [...this.addresses, ...this.companies];
  }

  public get countCreateActions() {
    if (this.canCreateAddress && this.canCreateCompany) return 2;
    if (!this.canCreateAddress && !this.canCreateCompany) return 0;
    return 1;
  }

  // Guarding

  public get canCreateAddress() {
    return this.$userCan("create_client_address");
  }

  public get canUpdateAddress() {
    return this.$userCan("update_client_address");
  }

  public get canDeleteAddress() {
    return this.$userCan("delete_client_address");
  }

  public get canCreateCompany() {
    return this.$userCan("create_client_company");
  }

  public get canUpdateCompany() {
    return this.$userCan("update_client_company");
  }

  public get canDeleteCompany() {
    return this.$userCan("delete_client_company");
  }

  public guardFunctionalities = [
    "list_client_addresses",
    "list_client_companies"
  ];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Provide

  @Provide("billableEntitiesProvider")
  public providerData(): IBillableEntitiesProvider {
    return {
      addresses: this.addresses,
      canCreateAddress: this.canCreateAddress,
      canCreateCompany: this.canCreateCompany,
      canDeleteAddress: this.canDeleteAddress,
      canDeleteCompany: this.canDeleteCompany,
      canUpdateAddress: this.canUpdateAddress,
      canUpdateCompany: this.canUpdateCompany,
      companies: this.companies,
      countCreateActions: this.countCreateActions,
      entities: this.entities,
      guardFunctionalities: this.guardFunctionalities,
      hasRequestError: this.hasRequestError,
      isLoading: this.isLoading,
      isRefreshingAddresses: this.isRefreshingAddresses,
      isRefreshingCompanies: this.isRefreshingCompanies,
      isReloading: this.isReloading
    };
  }

  @Provide("billableEntitiesProviderMethods")
  public get providerMethods(): IBillableEntitiesProviderMethods {
    return {
      deleteClientAddress: this.deleteClientAddress,
      deleteClientCompany: this.deleteClientCompany,
      manageClientAddress: this.manageClientAddress,
      manageClientCompany: this.manageClientCompany,
      refreshAddresses: this.refreshAddresses,
      refreshCompanies: this.refreshCompanies,
      reloadData: this.reloadData,
      setDefaultAddress: this.setDefaultAddress,
      setDefaultCompany: this.setDefaultCompany
    };
  }

  // Render

  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $billableEntitiesData: this.providerData(),
        $billableEntitiesMethods: this.providerMethods
      })
    );
  }
}
